import { useCallback, useEffect, useState } from 'react';

import loaderImage from 'assets/loader.gif';

import { Modal } from '../../../../UIPackage/ModalModule/components/Modal';
import { ModalHeader } from '../../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../../UIPackage/ModalModule/components/ModalActions';
import { IRequirementNpa } from '../../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import {
  fetchStructuralUnitsByOldFormat,
  fetchStructuresUnitsAsTree,
  structuralUnitConverter,
} from '../../../../APIPackage/RequirementsNpaModule/services/requirementsNpaApi';
import {
  IRequirementsMandatoryNew,
  IStructuresUnit,
} from '../../../../APIPackage/RequirementsNpaModule/interfaces/IStructuresUnitsAsTreeResponse';
import { StructuresUnitItem } from './StructuresUnitItem';
import styles from './RequirementNpaModal.module.scss';
import { Button } from '../../../../UIPackage/MainModule/components/Button';
import { IGetStructureUnitFlat } from '../../../../APIPackage/RequirementsNpaModule/interfaces/IGetStructureUnitFlat';
import { IRequirementStructuralUnitFilterForm } from '../../interfaces/IRequirementStructuralUnitFilterForm';

interface IRequirementNpaModalProps {
  selectedNpa: IRequirementNpa | undefined;
  onClose: () => void;
  onSubmit: (value: IRequirementStructuralUnitFilterForm[]) => void;
  withMandatory?: boolean;
}

export const RequirementNpaModal = ({
  selectedNpa,
  onClose,
  onSubmit,
  withMandatory = false,
}: IRequirementNpaModalProps) => {
  const [units, setUnits] = useState<IStructuresUnit[]>([]);
  const [selectedItems, setSelectedItems] = useState<{ [suId: string]: IRequirementsMandatoryNew[] }>({});
  const [isLoading, setIsLoading] = useState(true);

  const loadUnits = useCallback(
    async (npa: IRequirementNpa) => {
      const data = await fetchStructuresUnitsAsTree({
        npaId: npa?.id,
        withRequirements: withMandatory,
      });

      setUnits(data.nodes);
      setIsLoading(false);
    },
    [setUnits, setIsLoading],
  );

  useEffect(() => {
    if (selectedNpa) {
      setSelectedItems({});
      loadUnits(selectedNpa);
    } else {
      setUnits([]);
      setIsLoading(true);
    }
  }, [selectedNpa, setSelectedItems, withMandatory, setUnits]);

  const handleSubmit = useCallback(async () => {
    const params: IGetStructureUnitFlat = {
      units: [
        {
          npaId: selectedNpa?.id.toString() || '0',
          structuralUnits: Object.keys(selectedItems).map(item => ({
            id: item,
            mandatoryRequirementIds: [],
          })),
        },
      ],
    };

    const data = await fetchStructuralUnitsByOldFormat(params);
    const result: IRequirementStructuralUnitFilterForm[] = data[0].structuralUnits.map(item => ({
      structuralUnit: structuralUnitConverter(item.structuralUnit),
      mandatory: selectedItems[item.structuralUnit.id.toString()],
    }));

    onSubmit(result);
  }, [units, selectedItems, onSubmit, selectedNpa]);

  return (
    <Modal visible={!!selectedNpa} onClose={onClose}>
      <ModalHeader title="Добавление структурной единицы"></ModalHeader>
      <ModalBody>
        <div className={styles.Table}>
          <div className={styles.TableHeader}>
            <div>№ И ДАТА</div>
            <div>НАИМЕНОВАНИЕ НПА</div>
          </div>
          <div className={styles.TableBody}>
            <div className={styles.NpaRow}>
              <div>
                <span>{selectedNpa?.number}</span>
                <span>{selectedNpa?.acceptanceDate}</span>
              </div>
              <div>{selectedNpa?.title}</div>
            </div>
            {isLoading && (
              <div className={styles.LoaderWrapper}>
                <div className={styles.Loader} style={{ backgroundImage: `url(${loaderImage})` }} />
              </div>
            )}
            <div className={styles.Units}>
              {units.map((item, index) => (
                <StructuresUnitItem
                  key={index}
                  npaId={selectedNpa?.id || 0}
                  unit={item}
                  selectedItems={selectedItems}
                  setSelectedItems={setSelectedItems}
                  withMandatory={withMandatory}
                />
              ))}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalActions>
        <Button text="Далее" size="medium" color="primary" onClick={handleSubmit} />
        <Button text="Отмена" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};
